import React, {
  useRef,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

import type { GoogleAnalyticsItemWithIndex } from "types/events";

import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

export interface AnalyticsViewItemListProviderProps {
  /**
   * Google Analytics item_list_id attribute for product cards within this provider.
   */
  itemListId: string;
  /**
   * Google Analytics item_list_name attribute for product cards within this provider.
   */
  itemListName: string;
  /**
   * Render product cards within the provider.
   */
  children: React.ReactNode;
}

interface AnalyticsViewItemListContextType {
  pushItem: (item: GoogleAnalyticsItemWithIndex) => void;
  onClick: (item: GoogleAnalyticsItemWithIndex) => void;
}

const AnalyticsViewItemListContext =
  createContext<AnalyticsViewItemListContextType | null>(null);

export function AnalyticsViewItemListProvider({
  children,
  itemListId,
  itemListName,
}: AnalyticsViewItemListProviderProps) {
  const googleTagManager = useGoogleTagManager();

  /**
   * Array of items which are buffered for emit.
   */
  const bufferedItems = useRef<GoogleAnalyticsItemWithIndex[]>([]);

  /*
   * Array of item_id strings used to prevent the same
   * item being emitted multiple times.
   */
  const receivedItems = useRef<string[]>([]);

  /** Timer for emit */
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Emit the buffered items to Google Analytics in a view_item_list event */
  const emitEvent = useCallback(() => {
    // Buffer is empty, do nothing.
    if (0 === bufferedItems.current.length) return;

    // Emit event to Google Tag Manager.
    googleTagManager.push({
      event: "view_item_list",
      properties: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: bufferedItems.current,
      },
    });

    // Clear the buffer.
    bufferedItems.current = [];
  }, [googleTagManager]);

  /** Emit select_item event */
  const onClick = useCallback(
    (item: GoogleAnalyticsItemWithIndex) => {
      googleTagManager.push({
        event: "select_item",
        properties: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [item],
        },
      });
    },
    [googleTagManager],
  );

  /** Push item to the buffer for view_item_list event */
  const pushItem = useCallback((item: GoogleAnalyticsItemWithIndex) => {
    // Bail out if item has already been received.
    if (receivedItems.current.includes(item.item_id)) return;

    // Push item into received items array so it's not processed again.
    receivedItems.current.push(item.item_id);

    // Append item to buffer.
    bufferedItems.current.push(item);

    // Clear existing timeout if set.
    if (null !== timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Set a new timeout (2 seconds).
    timeoutId.current = setTimeout(() => {
      emitEvent();
    }, 2000);

    // If the buffer size exceeds the limit (12), emit now.
    if (12 <= bufferedItems.current.length) {
      // Clear the timeout since we're emitting now.
      clearTimeout(timeoutId.current);

      // Emit the event.
      emitEvent();
    }
  }, []);

  // Context value.
  const value = useMemo<AnalyticsViewItemListContextType>(
    () => ({ pushItem, onClick }),
    [pushItem, onClick],
  );

  // When unmounting, emit anything left in the buffer.
  useEffect(() => {
    return () => {
      emitEvent();
    };
  }, [emitEvent]);

  return (
    <AnalyticsViewItemListContext.Provider value={value}>
      {children}
    </AnalyticsViewItemListContext.Provider>
  );
}

export function useAnalyticsViewItemList() {
  return useContext(AnalyticsViewItemListContext);
}
